import { useState } from "preact/hooks"
import { Button } from "@/components/ui/button.tsx"
import { Input } from "@/components/ui/input.tsx"
import { Plus, ArrowUp, ArrowDown, Trash2 } from "lucide-react"

export interface DropdownOption {
  id: string
  value: string
  order: number
  matchPatterns: string[]
}

interface DropdownOptionsManagerProps {
  options: DropdownOption[]
  onOptionsChange: (options: DropdownOption[]) => void
  placeholder?: string
  className?: string
}

export function DropdownOptionsManager({ 
  options, 
  onOptionsChange, 
  placeholder = "Add option(s)... (comma-separated for multiple)",
  className = "w-84"
}: DropdownOptionsManagerProps) {
  const [newOptionValue, setNewOptionValue] = useState("")

  function addOption() {
    if (!newOptionValue.trim()) return

    // Check if the input contains commas (comma-separated list)
    if (newOptionValue.includes(',')) {
      // Split by comma and create multiple options
      const optionValues = newOptionValue
        .split(',')
        .map(val => val.trim())
        .filter(val => val.length > 0)

      const newOptions = optionValues.map((value, index) => ({
        id: `temp-${Date.now()}-${index}`,
        value: value,
        order: options.length + index,
        matchPatterns: [],
      }))

      const updatedOptions = [...options, ...newOptions]
      onOptionsChange(updatedOptions)
      setNewOptionValue("")
    } else {
      // Single option
      const newOption = {
        id: `temp-${Date.now()}`,
        value: newOptionValue.trim(),
        order: options.length,
        matchPatterns: [],
      }

      const updatedOptions = [...options, newOption]
      onOptionsChange(updatedOptions)
      setNewOptionValue("")
    }
  }

  function removeOption(optionId: string) {
    const updatedOptions = options.filter(opt => opt.id !== optionId)
    onOptionsChange(updatedOptions)
  }

  function moveOption(optionId: string, direction: 'up' | 'down') {
    const index = options.findIndex(opt => opt.id === optionId)
    if (index === -1) return

    const newOptions = [...options]
    const newIndex = direction === 'up' ? index - 1 : index + 1

    if (newIndex >= 0 && newIndex < newOptions.length) {
      [newOptions[index], newOptions[newIndex]] = [newOptions[newIndex], newOptions[index]]
      newOptions.forEach((opt, i) => {
        opt.order = i
      })
      onOptionsChange(newOptions)
    }
  }

  function updateOption(optionId: string, value: string) {
    const updatedOptions = options.map(opt =>
      opt.id === optionId ? { ...opt, value } : opt
    )
    onOptionsChange(updatedOptions)
  }

  function updateMatchPatterns(optionId: string, patternsStr: string) {
    // Parse comma-separated patterns
    const patterns = patternsStr
      .split(',')
      .map(p => p.trim().toLowerCase())
      .filter(p => p.length > 0)
    
    const updatedOptions = options.map(opt =>
      opt.id === optionId ? { ...opt, matchPatterns: patterns } : opt
    )
    onOptionsChange(updatedOptions)
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="font-medium">Dropdown Options</h4>
        <div className="flex gap-2">
          <Input
            value={newOptionValue}
            onChange={(e: Event) => {
              const target = e.target as HTMLInputElement
              setNewOptionValue(target.value)
            }}
            placeholder={placeholder}
            className={className}
          />
          <Button 
            // @ts-ignore
            size="sm" 
            onClick={addOption}>
            <Plus className="w-4 h-4 mr-1" />
            Add
          </Button>
        </div>
      </div>

      <div className="space-y-3">
        {options
          .sort((a, b) => a.order - b.order)
          .map((option, index) => (
            <div key={option.id} className="p-3 bg-gray-50 rounded space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500 w-8">{index + 1}</span>
                <Input
                  value={option.value}
                  onChange={(e: Event) => {
                    const target = e.target as HTMLInputElement
                    updateOption(option.id, target.value)
                  }}
                  className="flex-1"
                  placeholder="Option value"
                />
                <Button
                  // @ts-ignore
                  size="sm"
                  variant="outline"
                  onClick={() => moveOption(option.id, 'up')}
                  disabled={index === 0}
                >
                  <ArrowUp className="w-4 h-4" />
                </Button>
                <Button
                  // @ts-ignore
                  size="sm"
                  variant="outline"
                  onClick={() => moveOption(option.id, 'down')}
                  disabled={index === options.length - 1}
                >
                  <ArrowDown className="w-4 h-4" />
                </Button>
                <Button
                  // @ts-ignore
                  size="sm"
                  variant="outline"
                  onClick={() => removeOption(option.id)}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex items-center gap-2 pl-8">
                <span className="text-xs text-gray-500 w-24">Match patterns:</span>
                <Input
                  value={option.matchPatterns.join(', ')}
                  onChange={(e: Event) => {
                    const target = e.target as HTMLInputElement
                    updateMatchPatterns(option.id, target.value)
                  }}
                  className="flex-1 text-sm"
                  placeholder="e.g., jr, jun, junior (comma-separated aliases for fuzzy matching)"
                />
              </div>
            </div>
          ))}
      </div>
    </div>
  )
}
